import React, { useState, useEffect, useMemo, useCallback } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _isEqualWith from 'lodash/isEqualWith';
import _isArray from 'lodash/isArray';
import _some from 'lodash/some';
import _forEach from 'lodash/forEach';
import _filter from 'lodash/filter';
import _reduce from 'lodash/reduce';
import { WindowWidthContext } from '@minecraft.utils';
import Toggler from '@minecraft.toggler';
import { Formik, Form } from 'formik';
import { useTranslation } from '@blocs.i18n';
import { FilterValues, FilterProps } from '../shared/types';
import {
  useFilterPills,
  isEqualCustomizer,
  shouldClearButtonBeVisible,
  getClearedFilter,
  saveFilterUtilities,
} from '../shared/utils';
import { FilterBasicContainer } from './FilterBasicStyles';
import FilterBasicButton from './FilterBasicButton';
import FilterBasicToggler from './FilterBasicToggler';
import { FilterBasicTogglerTitle } from './FilterBasicToggler/FilterBasicTogglerStyles';
import FilterBasicForm from './FilterBasicForm';
import { FilterBasicFormContainer } from './FilterBasicForm/FilterBasicFormStyles';
import FilterBasicBreadbox from './FilterBasicBreadbox';

const FilterBasic = ({
  showAdvancedSearchButton,
  showFilterSettingsButton,
  showBreadbox = true,
  showFilterButton = true,
  hideClearButton = false,
  withFilterDrawer = false,
  isCollapsed = true,
  advancedSearchRoute,
  sessionStorageKey,
  filtersLabel,
  excludeValuesFromBreadbox,
  initialFilters,
  appliedFilters,
  onFiltersChange,
  onRetainedFiltersChange,
  children,
  className,
  // These Should only be used for adding additional filter options above and below the main filter container
  AboveFiltersFormComponent = null,
  BelowFiltersFormComponent = null,
  onNavigateAdvancedSearchRoute,
  SaveSearchButton = null,
}: FilterProps<FilterValues>) => {
  const { t } = useTranslation();
  const { lessSm } = WindowWidthContext.useWidthData();

  const [isFilterSettingsModal, setFilterSettingsModal] = useState(false);
  const [isFilterBasicOverlayOpened, setFilterBasicOverlayOpened] = useState(false);
  const [isFiltersFormDirty, setFiltersFormDirty] = useState(false);
  const [retainedFilters, setRetainedFilters] = useState<FilterValues>({
    ...initialFilters,
    ...appliedFilters,
  });

  const notAppliedFilters = useMemo(
    () =>
      _reduce(
        retainedFilters,
        (acc, _, key) => {
          if (_isArray(retainedFilters[key])) {
            return {
              ...acc,
              [key]: _filter(retainedFilters[key], (item) => !_some(appliedFilters[key], { value: item.value })),
            };
          }

          if (!_isEqual(retainedFilters[key], appliedFilters[key])) {
            return {
              ...acc,
              [key]: retainedFilters[key],
            };
          }

          return acc;
        },
        {}
      ),
    [appliedFilters, retainedFilters]
  );

  const appliedFilterPills = useFilterPills({
    filtersLabel,
    filters: appliedFilters,
    excludeValues: excludeValuesFromBreadbox,
  });

  const notAppliedFilterPills = useFilterPills({
    filtersLabel,
    filters: notAppliedFilters,
    excludeValues: excludeValuesFromBreadbox,
  });

  const isClearButtonVisible = useMemo(
    () =>
      !hideClearButton &&
      shouldClearButtonBeVisible({
        appliedFilterPills,
        appliedFilters,
        retainedFilters,
        initialFilters,
      }),
    [appliedFilterPills, appliedFilters, retainedFilters, initialFilters, hideClearButton]
  );
  const isBreadboxVisible = showBreadbox && (!_isEmpty(appliedFilterPills) || !_isEmpty(notAppliedFilterPills));

  const toggleFilterSettingsModal = useCallback((): void => {
    setFilterSettingsModal((prevState) => !prevState);
  }, []);

  const toggleFilterBasicOverlay = useCallback((): void => {
    setFilterBasicOverlayOpened((prevState) => !prevState);
  }, []);

  const handleRetainedChanges = useCallback(
    (newFilters: FilterValues): void => {
      // this allows parent implementations to manipulate
      // dependent fields without causing infinite loops

      // for example, if a given field should change based
      // on the change of a separate field then it should
      // detect that change in a custom onRetailedFiltersChange
      // and return the changes to the given field as part of
      // the output
      setRetainedFilters((prev) => {
        const newVal = { ...prev, ...newFilters };

        if (typeof onRetainedFiltersChange === 'function') {
          return onRetainedFiltersChange(newVal);
        }

        return newVal;
      });
    },
    [onRetainedFiltersChange]
  );

  const handleNotAppliedFiltersClear = useCallback(
    (filterKey?: string): void => {
      const newFiltersValues: FilterValues = {
        ...retainedFilters,
        [filterKey]: appliedFilters[filterKey],
      };
      handleRetainedChanges(newFiltersValues);
    },
    [retainedFilters, appliedFilters, handleRetainedChanges]
  );

  const handleAppliedFiltersClear = useCallback(
    (filterKey?: string, subKey?: string): void => {
      let cleanedFilters: Partial<FilterValues> = initialFilters;

      if (filterKey) {
        cleanedFilters = subKey
          ? ({
              [filterKey]: { ...retainedFilters[filterKey], [subKey]: undefined },
            } as Partial<FilterValues>)
          : getClearedFilter({ filterKey: filterKey as any, initialFilters });
      }

      const newFiltersValues = {
        ...retainedFilters,
        ...cleanedFilters,
      } as FilterValues;

      if (sessionStorageKey) {
        saveFilterUtilities.saveFiltersForThisSession({ storageKey: sessionStorageKey, filters: newFiltersValues });
      }

      onFiltersChange(newFiltersValues);
    },
    [retainedFilters, initialFilters, onFiltersChange, sessionStorageKey]
  );

  const handleRetainedFiltersClear = useCallback((): void => {
    if (sessionStorageKey) {
      saveFilterUtilities.clearSavedFiltersForThisSession({ storageKey: sessionStorageKey });
    }

    onFiltersChange(initialFilters);
    // we have to manually clear both sets here
    // because if someone hasn't applied filters and they
    // hit clear then applied wont change and trigger
    // a refresh to retained
    handleRetainedChanges(initialFilters);
  }, [initialFilters, handleRetainedChanges, onFiltersChange, sessionStorageKey]);

  const handleAppliedFiltersValueClear = (selectedFiltersValues: Record<string, any>): void => {
    const updatedAppliedFilters = { ...appliedFilters };

    _forEach(Object.keys(selectedFiltersValues), (key) => {
      if (_isArray(selectedFiltersValues[key])) {
        updatedAppliedFilters[key] = _filter(
          updatedAppliedFilters[key],
          (item) => !_some(selectedFiltersValues[key], { value: item.value })
        );
      } else {
        updatedAppliedFilters[key] = initialFilters[key];
      }
    });

    if (sessionStorageKey) {
      saveFilterUtilities.saveFiltersForThisSession({ storageKey: sessionStorageKey, filters: updatedAppliedFilters });
    }

    onFiltersChange(updatedAppliedFilters);
  };

  useEffect(() => {
    // this happens when the filters are reset in the parent level when:
    // parent form sets initial filters on page load
    // a user selects a saved search to apply
    // a user updates applied filters by clicking the "Filter" button
    // a user clears filters with the "Clear" button
    handleRetainedChanges(appliedFilters);
  }, [appliedFilters, handleRetainedChanges]);

  useEffect(() => {
    if (!isFiltersFormDirty && !_isEqualWith(appliedFilters, retainedFilters, isEqualCustomizer)) {
      setFiltersFormDirty(true);
    }

    if (isFiltersFormDirty && _isEqualWith(appliedFilters, retainedFilters, isEqualCustomizer)) {
      setFiltersFormDirty(false);
    }
  }, [appliedFilters, retainedFilters, isFiltersFormDirty]);

  const handleSubmit = useCallback(
    (values): void => {
      if (sessionStorageKey) {
        saveFilterUtilities.saveFiltersForThisSession({ storageKey: sessionStorageKey, filters: values });
      }

      onFiltersChange(values);

      if (lessSm) {
        toggleFilterBasicOverlay();
      }
    },
    [sessionStorageKey, onFiltersChange, lessSm, toggleFilterBasicOverlay]
  );

  return (
    <FilterBasicContainer className={className} data-testid="bloc-filter-basic" data-qa-id="schedule-filter-toggle">
      <Formik initialValues={retainedFilters} enableReinitialize onSubmit={handleSubmit}>
        {(): JSX.Element => (
          <Form>
            <>
              {lessSm && (
                <>
                  {AboveFiltersFormComponent && (
                    <div className="cn_mb-4" data-testid="above-filter-form-component-container">
                      {AboveFiltersFormComponent}
                    </div>
                  )}
                  <FilterBasicButton
                    areFiltersApplied={!showBreadbox && !_isEmpty(appliedFilterPills)}
                    isFiltersFormDirty={isFiltersFormDirty}
                    toggleFilterBasicOverlay={toggleFilterBasicOverlay}
                  />
                  {isFilterBasicOverlayOpened && (
                    <FilterBasicForm
                      isClearButtonVisible={isClearButtonVisible}
                      showFilterSettingsButton={showFilterSettingsButton}
                      showAdvancedSearchButton={showAdvancedSearchButton}
                      showFilterButton={showFilterButton}
                      withFilterDrawer={withFilterDrawer}
                      advancedSearchRoute={advancedSearchRoute}
                      onNavigateAdvancedSearchRoute={onNavigateAdvancedSearchRoute}
                      sessionStorageKey={sessionStorageKey}
                      retainedFilters={retainedFilters}
                      toggleFilterSettingsModal={toggleFilterSettingsModal}
                      toggleFilterBasicOverlay={toggleFilterBasicOverlay}
                      onClear={handleRetainedFiltersClear}
                      onRetainedFiltersChange={handleRetainedChanges}
                      onFiltersChange={onFiltersChange}
                    >
                      {children({ isFilterSettingsModal, toggleFilterSettingsModal })}
                    </FilterBasicForm>
                  )}
                  {BelowFiltersFormComponent && (
                    <div className="cn_mt-4" data-testid="below-filter-form-component-container">
                      {BelowFiltersFormComponent}
                    </div>
                  )}
                </>
              )}
              {!lessSm && (
                <>
                  {AboveFiltersFormComponent && (
                    <div className="cn_mb-6" data-testid="above-filter-form-component-container">
                      {AboveFiltersFormComponent}
                    </div>
                  )}
                  <Toggler
                    isDefaultOpen={isCollapsed}
                    noMargin
                    render={(): JSX.Element => (
                      <FilterBasicToggler
                        isClearButtonVisible={isClearButtonVisible}
                        onClear={handleRetainedFiltersClear}
                      />
                    )}
                  >
                    <FilterBasicForm
                      isClearButtonVisible={false}
                      showFilterSettingsButton={showFilterSettingsButton}
                      showAdvancedSearchButton={showAdvancedSearchButton}
                      showFilterButton={showFilterButton}
                      withFilterDrawer={withFilterDrawer}
                      advancedSearchRoute={advancedSearchRoute}
                      sessionStorageKey={sessionStorageKey}
                      retainedFilters={retainedFilters}
                      toggleFilterSettingsModal={toggleFilterSettingsModal}
                      toggleFilterBasicOverlay={toggleFilterBasicOverlay}
                      onClear={handleRetainedFiltersClear}
                      onRetainedFiltersChange={handleRetainedChanges}
                      onFiltersChange={onFiltersChange}
                      onNavigateAdvancedSearchRoute={onNavigateAdvancedSearchRoute}
                    >
                      {children({ isFilterSettingsModal, toggleFilterSettingsModal })}
                    </FilterBasicForm>
                  </Toggler>
                  {BelowFiltersFormComponent && (
                    <div className="cn_mt-6" data-testid="below-filter-form-component-container">
                      {BelowFiltersFormComponent}
                    </div>
                  )}
                  {SaveSearchButton}
                  {isBreadboxVisible && (
                    <>
                      {!_isEmpty(notAppliedFilterPills) && (
                        <FilterBasicBreadbox
                          isNotAppliedFilterPills
                          label={t('common:filter.notApplied')}
                          pills={notAppliedFilterPills}
                          onClear={handleNotAppliedFiltersClear}
                        />
                      )}
                      {!_isEmpty(appliedFilterPills) && (
                        <FilterBasicBreadbox
                          label={t('common:filter.appliedFilters')}
                          pills={appliedFilterPills}
                          onClear={handleAppliedFiltersClear}
                          onFiltersValueClear={handleAppliedFiltersValueClear}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </>
          </Form>
        )}
      </Formik>
    </FilterBasicContainer>
  );
};

FilterBasic.Container = FilterBasicContainer;
FilterBasic.Toggler = Toggler;
FilterBasic.ToglerTitle = FilterBasicTogglerTitle;
FilterBasic.FormContainer = FilterBasicFormContainer;

export default FilterBasic;
